import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { PasswordGuard } from './password-guard';
import { AppLayoutComponent } from './layouts/app-layout/app-layout.component';
import { AuthGuard } from './auth-guard.service';
import { LoginRedirectGuard } from './login-redirect.guard';
import { PageAccessGuard } from './page-access-auth.guard';
import { MODULE_READ } from './constants/permissions';
import { NavAppTitleResolver } from './pages/nav-app/titleResolverNavApp.service';

const routes: Routes = [
  {
    path: 'login',
    loadChildren: () =>
      import('./pages/login-page/login-page.module').then(
        (m) => m.LoginPageModule,
      ),
    pathMatch: 'full',
    data: { title: 'Login' },
  },
  {
    path: 'sso-logout',
    pathMatch: 'full',
    loadChildren: () =>
      import('./pages/sso-logout/sso-logout.module').then(
        (m) => m.SSOLgoutPageModule,
      ),
    data: { title: 'SSO Logout' },
  },
  {
    path: 'forgot-password',
    loadChildren: () =>
      import(
        './pages/password-management/forgot-password/forgot-password.module'
      ).then((m) => m.ForgotPasswordModule),
    data: { title: 'Forgot password' },
  },
  {
    path: 'reset-password',
    loadChildren: () =>
      import(
        './pages/password-management/reset-password/reset-password.module'
      ).then((m) => m.ResetPasswordModule),
    data: { title: 'Save password' },
  },
  {
    path: '',
    redirectTo: 'home',
    pathMatch: 'full',
  },
  {
    path: '',
    component: AppLayoutComponent,
    data: { title: 'Project', level: 1 },
    canActivate: [LoginRedirectGuard, AuthGuard],
    children: [
      {
        path: 'components',
        loadChildren: () =>
          import('./pages/component-tests/component-tests.module').then(
            (m) => m.ComponentTestsModule,
          ),
        data: { title: 'Components', level: 1 },
      },
      {
        path: 'home',
        loadChildren: () => {
          return import('./pages/projects/projects.module').then(
            (m) => m.ProjectsModule,
          );
        },
        data: { title: 'Projects', level: 1 },
      },
      {
        path: 'nav-app/:name',
        loadChildren: () => {
          return import('./pages/nav-app/nav-app.module').then(
            (m) => m.NavAppModule,
          );
        },
        data: { level: 1 },
        resolve: {
          title: NavAppTitleResolver,
        },
      },
      {
        path: 'no-access',
        loadChildren: () =>
          import('./pages/no-access/no-access.module').then(
            (m) => m.NoAccessModule,
          ),
        data: { title: 'Inaccessible', level: 1 },
      },
      {
        path: 'ops-center',
        loadChildren: () =>
          import('./pages/ops-center/ops-center.module').then(
            (m) => m.OpsCenterModule,
          ),
        data: {
          title: 'Ops Center',
          level: 1,
        },
      },
      {
        path: 'settings',
        loadChildren: () =>
          import('./pages/settings/settings.module').then(
            (m) => m.SettingsModule,
          ),
        data: {
          title: 'Organization Settings',
          level: 1,
        },
      },
      {
        path: 'artifacts/:registry',
        loadChildren: () =>
          import('./pages/artifacts/artifacts.module').then(
            (m) => m.ArtifactsModule,
          ),
        data: { title: 'Artifacts', level: 1 },
      },
      {
        path: 'trash',
        loadChildren: () =>
          import(
            './pages/soft-delete-entities/soft-delete-entities.module'
          ).then((m) => m.SoftDeleteEntitiesModule),
        data: { title: 'Trash', level: 1 },
      },
      {
        path: 'accountSettings',
        loadChildren: () =>
          import('./pages/account-settings/account-settings.module').then(
            (m) => m.AccountSettingsModule,
          ),
        data: { title: 'Account Settings', level: 1 },
      },
      {
        path: 'createStack',
        loadChildren: () =>
          import(
            './pages/cluster-pages/stack-create-v2/stack-create-v2.module'
          ).then((m) => m.StackCreateV2Module),
        data: { title: 'Create blueprint', level: 2 }, // Blueprints > Create Blueprint
      },
      {
        path: 'artifact-ci-details/:ciId',
        loadChildren: () =>
          import('./pages/artifacts-pages/ci-details/ci-details.module').then(
            (m) => m.CIDetailsModule,
          ),
        data: { title: 'Artifacts', level: 1 },
      },
      {
        path: 'artifact-history/:versioningKey/ciId/:ciId',
        loadChildren: () =>
          import(
            './pages/artifacts-pages/artifact-history/artifact-history.module'
          ).then((m) => m.ArtifactHistoryModule),
        data: { title: 'Artifacts', level: 1 },
      },
      {
        path: 'stack/:stackName',
        loadChildren: () =>
          import('./pages/stack/stack.module').then((m) => {
            return m.StackModule;
          }),
        data: { title: 'Project', level: 1 },
      },
      {
        path: ':stackName/chooseClusterCreate',
        loadChildren: () =>
          import(
            './pages/cluster-pages/cluster-chooser/cluster-chooser.module'
          ).then((m) => m.ClusterChooserModule),
        data: { title: 'Create environment', level: 2 },
      },
      {
        path: 'stack/:stackName/edit',
        loadChildren: () =>
          import(
            './pages/cluster-pages/stack-create-v2/stack-create-v2.module'
          ).then((m) => m.StackCreateV2Module),
        data: { title: 'Edit blueprint', level: 2 },
      },
      {
        path: 'dashboards',
        loadChildren: () =>
          import('./pages/dashboards/dashboards.module').then(
            (m) => m.DashboardsModule,
          ),
      },
      {
        path: 'central-audit-logs',
        loadChildren: () =>
          import('./pages/central-audit-logs/central-audit-logs.module').then(
            (m) => m.CentralAuditLogsModule,
          ),
        data: {
          title: 'Audit Logs',
          level: 1,
        },
      },
      {
        path: 'billing',
        loadChildren: () =>
          import('./pages/saas-billing/saas-billing.module').then(
            (m) => m.SaaSBilingModule,
          ),
        data: {
          title: 'Billing',
          level: 1,
        },
      },
      {
        path: 'software-engineering-guidelines',
        loadChildren: () =>
          import(
            './pages/merc-guideline-panel/merc-guideline-panel.module'
          ).then((m) => m.MercGuidelinePanelModule),
        data: {
          title: 'Software Engineering Guidelines',
          level: 1,
        },
      },
    ],
  },
  { path: '**', redirectTo: '/home' },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      paramsInheritanceStrategy: 'always',
      onSameUrlNavigation: 'reload',
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
